import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
function FadeInUp({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay
  }} data-id="element-189">
      {children}
    </motion.div>;
}
export function HowItWorksSection() {
  return <section className="w-full py-20 px-4 relative overflow-hidden" style={{
    background: 'radial-gradient(ellipse at 50% 100%, #0f1f3d 0%, #0a1628 60%)'
  }} data-id="element-190">
      <div className="max-w-4xl mx-auto" data-id="element-191">
        <FadeInUp data-id="element-192">
          <div className="text-center mb-16" data-id="element-193">
            <div className="section-divider mb-6" data-id="element-194">
              <span className="ornament" data-id="element-195">⟡</span>
            </div>
            <h2 className="font-heading font-700 text-4xl md:text-5xl mb-4" style={{
            color: 'var(--color-cream)'
          }} data-id="element-196">
              Las 3 Fases de Tu
              <span className="block italic" style={{
              color: 'var(--color-gold)'
            }} data-id="element-197">
                Transformación
              </span>
            </h2>
          </div>
        </FadeInUp>

        <div className="relative" data-id="element-198">
          {/* Vertical connecting line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px hidden sm:block" style={{
          background: 'linear-gradient(180deg, transparent, var(--color-gold-dim), var(--color-gold-dim), transparent)',
          transform: 'translateX(-50%)'
        }} data-id="element-199" />

          <div className="space-y-12" data-id="element-200">
            {/* Phase 1 */}
            <FadeInUp delay={0.1} data-id="element-201">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-202">
                <div className="flex-1 md:text-right order-2 md:order-1" data-id="element-203">
                  <div className="rounded-xl p-6 inline-block w-full" style={{
                  background: 'rgba(15, 31, 61, 0.8)',
                  border: '1px solid rgba(201, 168, 76, 0.2)'
                }} data-id="element-204">
                    <span className="inline-block font-body text-xs tracking-widest uppercase mb-2 px-2 py-1 rounded" style={{
                    background: 'rgba(180, 60, 60, 0.15)',
                    color: '#e07070',
                    border: '1px solid rgba(180, 60, 60, 0.2)'
                  }} data-id="element-205">
                      Días 1–7
                    </span>
                    <h3 className="font-heading font-600 text-2xl mb-3" style={{
                    color: 'var(--color-gold)'
                  }} data-id="element-206">
                      Fase de Limpieza
                    </h3>
                    <p className="font-body text-base leading-relaxed" style={{
                    color: 'var(--color-cream-dim)'
                  }} data-id="element-207">
                      Eliminamos los "incendiarios" inflamatorios de tu dieta.
                      Tu cuerpo comienza a liberar la carga acumulada de años de
                      alimentación incorrecta.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-heading font-700 text-2xl z-10 order-1 md:order-2" style={{
                background: 'var(--color-navy)',
                border: '2px solid var(--color-gold)',
                color: 'var(--color-gold)',
                boxShadow: '0 0 20px rgba(201, 168, 76, 0.3)'
              }} data-id="element-208">
                  I
                </div>
                <div className="flex-1 order-3 hidden md:block" data-id="element-209" />
              </div>
            </FadeInUp>

            {/* Phase 2 */}
            <FadeInUp delay={0.2} data-id="element-210">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-211">
                <div className="flex-1 hidden md:block order-1" data-id="element-212" />
                <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-heading font-700 text-2xl z-10 order-1 md:order-2" style={{
                background: 'var(--color-navy)',
                border: '2px solid var(--color-blue-glow)',
                color: 'var(--color-blue-glow)',
                boxShadow: '0 0 20px rgba(74, 158, 255, 0.3)'
              }} data-id="element-213">
                  II
                </div>
                <div className="flex-1 order-2 md:order-3" data-id="element-214">
                  <div className="rounded-xl p-6 w-full" style={{
                  background: 'rgba(15, 31, 61, 0.8)',
                  border: '1px solid rgba(74, 158, 255, 0.2)'
                }} data-id="element-215">
                    <span className="inline-block font-body text-xs tracking-widest uppercase mb-2 px-2 py-1 rounded" style={{
                    background: 'rgba(74, 158, 255, 0.1)',
                    color: 'var(--color-blue-glow)',
                    border: '1px solid rgba(74, 158, 255, 0.2)'
                  }} data-id="element-216">
                      Días 8–14
                    </span>
                    <h3 className="font-heading font-600 text-2xl mb-3" style={{
                    color: 'var(--color-blue-glow)'
                  }} data-id="element-217">
                      Fase de Activación
                    </h3>
                    <p className="font-body text-base leading-relaxed" style={{
                    color: 'var(--color-cream-dim)'
                  }} data-id="element-218">
                      Implementamos el ayuno intermitente bíblico para iniciar
                      la autolimpieza celular. Tu cuerpo activa su mecanismo de
                      regeneración profunda.
                    </p>
                  </div>
                </div>
              </div>
            </FadeInUp>

            {/* Phase 3 */}
            <FadeInUp delay={0.3} data-id="element-219">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-220">
                <div className="flex-1 md:text-right order-2 md:order-1" data-id="element-221">
                  <div className="rounded-xl p-6 inline-block w-full" style={{
                  background: 'rgba(15, 31, 61, 0.8)',
                  border: '1px solid rgba(74, 124, 89, 0.3)'
                }} data-id="element-222">
                    <span className="inline-block font-body text-xs tracking-widest uppercase mb-2 px-2 py-1 rounded" style={{
                    background: 'rgba(74, 124, 89, 0.12)',
                    color: 'var(--color-olive)',
                    border: '1px solid rgba(74, 124, 89, 0.3)'
                  }} data-id="element-223">
                      Días 15–21
                    </span>
                    <h3 className="font-heading font-600 text-2xl mb-3" style={{
                    color: 'var(--color-olive)'
                  }} data-id="element-224">
                      Fase de Vitalidad
                    </h3>
                    <p className="font-body text-base leading-relaxed" style={{
                    color: 'var(--color-cream-dim)'
                  }} data-id="element-225">
                      Consolidamos nuevos hábitos de movimiento y nutrición para
                      mantener los resultados de por vida. Tu nuevo estado
                      normal.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-heading font-700 text-2xl z-10 order-1 md:order-2" style={{
                background: 'var(--color-navy)',
                border: '2px solid var(--color-olive)',
                color: 'var(--color-olive)',
                boxShadow: '0 0 20px rgba(74, 124, 89, 0.3)'
              }} data-id="element-226">
                  III
                </div>
                <div className="flex-1 order-3 hidden md:block" data-id="element-227" />
              </div>
            </FadeInUp>
          </div>
        </div>
      </div>
    </section>;
}